import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { supabaseBadgeService } from '../lib/supabaseBadgeService'
import type { UserBadgeWithDetails } from '../lib/supabaseBadgeService'

interface BadgeNotification {
  badge: UserBadgeWithDetails['badge']
  earnedAt: string
  isNew: boolean
}

export function useBadgeNotifications(userId: string) {
  const [notifications, setNotifications] = useState<BadgeNotification[]>([])
  const [currentNotification, setCurrentNotification] = useState<BadgeNotification | null>(null)
  const lastBadgeCountRef = useRef<number>(0)

  useEffect(() => {
    if (!userId) return

    // Subscribe to real-time badge updates
    const badgeSubscription = supabase
      .channel('user-badges')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_badges',
          filter: `user_id=eq.${userId}`
        },
        async (payload) => {
          console.log('New badge earned:', payload)

          // Fetch the complete badge details
          const { data: badgeDetails } = await supabase
            .from('user_badges')
            .select(`
              *,
              badges (*)
            `)
            .eq('id', payload.new.id)
            .single()

          if (badgeDetails) {
            const notification: BadgeNotification = {
              badge: (badgeDetails as any).badges,
              earnedAt: (badgeDetails as any).earned_at,
              isNew: true
            }

            // Add to notifications queue
            setNotifications(prev => [...prev, notification])

            // Show current notification
            setCurrentNotification(notification)
          }
        }
      )
      .subscribe()

    // Check for new badges on mount (in case user earned badges while offline)
    const checkNewBadges = async () => {
      try {
        const userBadges = await supabaseBadgeService.getUserBadges(userId)
        const currentCount = userBadges.length

        if (lastBadgeCountRef.current > 0 && currentCount > lastBadgeCountRef.current) {
          // User earned badges while we weren't watching
          const newBadges = userBadges.slice(-(currentCount - lastBadgeCountRef.current))

          newBadges.forEach((userBadge: any) => {
            const notification: BadgeNotification = {
              badge: userBadge.badge,
              earnedAt: userBadge.earned_at,
              isNew: true
            }

            setNotifications(prev => [...prev, notification])
          })

          // Show the most recent notification
          if (newBadges.length > 0) {
            const latestBadge: any = newBadges[newBadges.length - 1]
            setCurrentNotification({
              badge: latestBadge.badge,
              earnedAt: latestBadge.earned_at,
              isNew: true
            })
          }
        }

        lastBadgeCountRef.current = currentCount
      } catch (error) {
        console.error('Failed to check new badges:', error)
      }
    }

    checkNewBadges()

    return () => {
      badgeSubscription.unsubscribe()
    }
  }, [userId])

  const dismissNotification = () => {
    setCurrentNotification(null)
    // Remove from queue after a delay
    setTimeout(() => {
      setNotifications(prev => prev.slice(1))
    }, 300)
  }

  const showNextNotification = () => {
    if (notifications.length > 1) {
      setCurrentNotification(notifications[1])
    } else {
      setCurrentNotification(null)
    }
  }

  return {
    currentNotification,
    hasNotifications: notifications.length > 0,
    dismissNotification,
    showNextNotification
  }
}